/* ============================================================================
   Mansa team page - sticker collage
   Loaded as a linked asset (Turbo does not reliably bundle section JS).

   Design rules this file obeys:
   - Prose is visible WITHOUT this file. Everything here is enhancement only.
     The `js` class is what opts the CSS into hiding-then-revealing, so if this
     script never runs, every bio still renders.
   - Video: poster first, no <source> until near viewport, only the most-visible
     card plays (1 on mobile, 2 on desktop), reduced-motion/Data-Saver = posters.
   - One controller per section instance, fully torn down on section unload, so
     the theme editor can add/remove/reorder blocks without leaking observers.
   ========================================================================= */
(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var saveData = !!(navigator.connection && navigator.connection.saveData);
  var hasFinePointer = window.matchMedia('(hover:hover) and (pointer:fine)').matches;

  var instances = {};   /* sectionId -> Controller */

  function Controller(root) {
    this.root = root;
    this.id = root.dataset.sectionId;
    this.motionOff = reduceMotion.matches || saveData;
    this.videos = [];
    this.ratios = new WeakMap();
    this.listeners = [];
    this.trailRaf = null;
    this.parallaxRaf = null;
    this.destroyed = false;
    this.init();
  }

  Controller.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.listeners.push([target, type, fn, opts]);
  };

  Controller.prototype.init = function () {
    var self = this;
    this.root.classList.toggle('mtc--motion-off', this.motionOff);

    this.splitChars();
    this.setupReveal();
    this.setupVideo();
    this.setupParallax();
    this.setupCursor();

    /* hero entrance */
    requestAnimationFrame(function () {
      if (!self.destroyed) self.root.classList.add('mtc--ready');
    });

    /* respond to a live change of the OS reduced-motion setting */
    this.mqHandler = function () {
      self.motionOff = reduceMotion.matches || saveData;
      self.root.classList.toggle('mtc--motion-off', self.motionOff);
      if (self.motionOff) self.pauseAll(); else self.tick();
    };
    if (reduceMotion.addEventListener) {
      reduceMotion.addEventListener('change', this.mqHandler);
    }
  };

  /* ---- split names into per-character spans for the stagger ------------- */
  Controller.prototype.splitChars = function () {
    var targets = this.root.querySelectorAll('[data-mtc-split]');
    Array.prototype.forEach.call(targets, function (el) {
      if (el.dataset.mtcSplitDone) return;          /* idempotent */
      el.dataset.mtcSplitDone = '1';
      var text = el.textContent;
      var html = '';
      for (var i = 0; i < text.length; i++) {
        var c = text.charAt(i);
        html += c === ' '
          ? ' '
          : '<span class="mtc-ch" style="animation-delay:' + (i * 38) + 'ms;transition-delay:' +
            (i * 26) + 'ms">' + c.replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</span>';
      }
      el.innerHTML = html;
    });
  };

  /* ---- scroll reveal ---------------------------------------------------- */
  Controller.prototype.setupReveal = function () {
    var self = this;
    var people = this.root.querySelectorAll('.mtc__person');
    if (!('IntersectionObserver' in window)) {
      /* no IO support: show everything immediately rather than hiding content */
      Array.prototype.forEach.call(people, function (p) { p.classList.add('is-in'); });
      return;
    }
    this.revealIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          self.revealIO.unobserve(e.target);
        }
      });
    }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
    Array.prototype.forEach.call(people, function (p) { self.revealIO.observe(p); });
  };

  /* ---- lazy video ------------------------------------------------------- */
  Controller.prototype.setupVideo = function () {
    var self = this;
    var vids = this.root.querySelectorAll('video[data-src]');
    if (!vids.length) return;

    Array.prototype.forEach.call(vids, function (v) {
      v.preload = 'none';
      v.muted = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('muted', '');
      v.setAttribute('playsinline', '');
      self.videos.push(v);
      self.on(v, 'playing', function () { v.classList.add('is-playing'); });
      self.on(v, 'pause', function () { v.classList.remove('is-playing'); });
    });

    if (!('IntersectionObserver' in window)) return;

    this.videoIO = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        self.ratios.set(e.target, e.isIntersecting ? e.intersectionRatio : 0);
        if (e.intersectionRatio > 0.05 && !self.motionOff) self.attach(e.target);
      });
      self.tick();
    }, { threshold: [0, 0.05, 0.25, 0.5, 0.75, 1] });

    this.videos.forEach(function (v) { self.videoIO.observe(v); });
  };

  Controller.prototype.attach = function (v) {
    if (v.dataset.attached) return;
    v.dataset.attached = '1';
    var s = document.createElement('source');
    s.src = v.dataset.src;
    s.type = v.dataset.type || 'video/mp4';
    v.appendChild(s);
    v.load();
  };

  Controller.prototype.pauseAll = function () {
    this.videos.forEach(function (v) { if (!v.paused) v.pause(); });
  };

  Controller.prototype.tick = function () {
    if (this.destroyed || this.motionOff) return;
    var self = this;
    var cap = window.innerWidth < 768 ? 1 : 2;
    var winners = this.videos
      .map(function (v) { return { v: v, r: self.ratios.get(v) || 0 }; })
      .filter(function (o) { return o.r > 0.25; })
      .sort(function (a, b) { return b.r - a.r; })
      .slice(0, cap)
      .map(function (o) { return o.v; });

    this.videos.forEach(function (v) {
      if (winners.indexOf(v) === -1) {
        if (!v.paused) v.pause();
      } else {
        self.attach(v);
        if (v.paused) {
          var p = v.play();
          /* autoplay can still be refused; the poster stands in, so swallow it */
          if (p && p.catch) p.catch(function () {});
        }
      }
    });
  };

  /* ---- sticker parallax ------------------------------------------------- */
  Controller.prototype.setupParallax = function () {
    var self = this;
    var stickers = this.root.querySelectorAll('.mtc-sticker');
    if (!stickers.length) return;

    var items = Array.prototype.map.call(stickers, function (el, i) {
      return { el: el, k: (i % 4) * 0.06 + 0.05 };
    });

    function frame() {
      self.parallaxRaf = null;
      if (self.destroyed) return;
      var vh = window.innerHeight;
      items.forEach(function (s) {
        var r = s.el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;   /* skip off-screen work */
        var off = (vh / 2 - (r.top + r.height / 2)) * s.k;
        s.el.style.transform = 'translateY(' + off.toFixed(1) + 'px)';
      });
    }

    this.on(window, 'scroll', function () {
      if (self.motionOff || self.destroyed) return;
      if (!self.parallaxRaf) self.parallaxRaf = requestAnimationFrame(frame);
    }, { passive: true });
  };

  /* ---- cursor sticker ---------------------------------------------------
     Only on fine pointers, and the rAF loop sleeps once it catches up. A
     permanently-running rAF burns battery on devices that never show a cursor. */
  Controller.prototype.setupCursor = function () {
    if (!hasFinePointer) return;
    var self = this;
    var cs = this.root.querySelector('.mtc__cursor');
    if (!cs) return;
    var cx = 0, cy = 0, tx = 0, ty = 0;

    function trail() {
      cx += (tx - cx) * 0.14;
      cy += (ty - cy) * 0.14;
      cs.style.transform = 'translate(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px)';
      if (!self.destroyed && (Math.abs(tx - cx) > 0.5 || Math.abs(ty - cy) > 0.5)) {
        self.trailRaf = requestAnimationFrame(trail);
      } else {
        self.trailRaf = null;
      }
    }

    this.on(window, 'mousemove', function (e) {
      if (self.destroyed) return;
      tx = e.clientX + 14; ty = e.clientY + 14;
      cs.style.opacity = '0.9';
      if (!self.trailRaf) self.trailRaf = requestAnimationFrame(trail);
    }, { passive: true });

    this.on(this.root, 'mouseover', function (e) {
      var person = e.target.closest ? e.target.closest('.mtc__person') : null;
      if (person && person.dataset.prop) cs.textContent = person.dataset.prop;
    });
  };

  /* ---- teardown --------------------------------------------------------- */
  Controller.prototype.destroy = function () {
    this.destroyed = true;
    if (this.revealIO) this.revealIO.disconnect();
    if (this.videoIO) this.videoIO.disconnect();
    if (this.trailRaf) cancelAnimationFrame(this.trailRaf);
    if (this.parallaxRaf) cancelAnimationFrame(this.parallaxRaf);
    this.pauseAll();
    this.listeners.forEach(function (l) { l[0].removeEventListener(l[1], l[2], l[3]); });
    this.listeners = [];
    if (this.mqHandler && reduceMotion.removeEventListener) {
      reduceMotion.removeEventListener('change', this.mqHandler);
    }
    delete instances[this.id];
  };

  /* ---- boot / Shopify theme-editor lifecycle ---------------------------- */
  function mount(scope) {
    var roots = (scope || document).querySelectorAll('.mtc[data-section-id]');
    Array.prototype.forEach.call(roots, function (root) {
      var id = root.dataset.sectionId;
      if (instances[id]) instances[id].destroy();     /* idempotent re-init */
      instances[id] = new Controller(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { mount(document); });
  } else {
    mount(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector && e.target.querySelector('.mtc[data-section-id]')) mount(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector ? e.target.querySelector('.mtc[data-section-id]') : null;
    if (root && instances[root.dataset.sectionId]) instances[root.dataset.sectionId].destroy();
  });

  /* When staff click a block in the editor, that card must be fully visible and
     never left stuck mid-reveal. */
  document.addEventListener('shopify:block:select', function (e) {
    var person = e.target.closest ? e.target.closest('.mtc__person') : null;
    if (!person) return;
    person.classList.add('is-in', 'is-editor-selected');
    var v = person.querySelector('video[data-src]');
    var inst = instances[person.closest('.mtc').dataset.sectionId];
    if (v && inst && !inst.motionOff) {
      inst.attach(v);
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    }
  });

  document.addEventListener('shopify:block:deselect', function (e) {
    var person = e.target.closest ? e.target.closest('.mtc__person') : null;
    if (person) person.classList.remove('is-editor-selected');
  });
})();
